import { ActionIcon, Button, Group, Stack, Text, Textarea } from '@mantine/core'
import { Dropzone, MIME_TYPES } from '@mantine/dropzone'
import { showNotification } from '@mantine/notifications'
import type { Attachment } from '@medplum/fhirtypes'
import { IconFile, IconUpload, IconX } from '@tabler/icons-react'
import { useState } from 'react'

const MAX_ATTACHMENTS = 5
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

type Props = {
  initialNote?: string
  initialNextAction?: string
  initialAttachments?: Attachment[]
  onSubmit: (
    note: string,
    nextAction: string,
    newFiles: File[],
    keptAttachments: Attachment[],
  ) => Promise<void>
  onCancel: () => void
}

export function NoteForm({
  initialNote = '',
  initialNextAction = '',
  initialAttachments = [],
  onSubmit,
  onCancel,
}: Props) {
  const [note, setNote] = useState(initialNote)
  const [nextAction, setNextAction] = useState(initialNextAction)
  const [newFiles, setNewFiles] = useState<File[]>([])
  const [keptAttachments, setKeptAttachments] = useState<Attachment[]>(initialAttachments)
  const [loading, setLoading] = useState(false)

  const totalFiles = keptAttachments.length + newFiles.length
  const remainingSlots = MAX_ATTACHMENTS - totalFiles

  const handleDrop = (dropped: File[]) => {
    setNewFiles((prev) => [...prev, ...dropped.slice(0, remainingSlots)])
  }

  const handleSubmit = async () => {
    if (!note.trim()) return
    setLoading(true)
    try {
      await onSubmit(note.trim(), nextAction.trim(), newFiles, keptAttachments)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack gap="md">
      <Textarea
        label="Note"
        placeholder="Enter care coordination note..."
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
        minRows={4}
        autosize
        required
      />
      <Textarea
        label="Next Action"
        placeholder="Enter next action (optional)..."
        value={nextAction}
        onChange={(e) => setNextAction(e.currentTarget.value)}
        minRows={3}
        autosize
      />

      <div>
        <Text size="sm" fw={500} mb={4}>
          Attachments{totalFiles > 0 ? ` (${totalFiles}/${MAX_ATTACHMENTS})` : ''}
        </Text>

        {totalFiles > 0 && (
          <Stack gap={4} mb="xs">
            {keptAttachments.map((a, i) => (
              <Group
                key={`kept-${i}`}
                gap="xs"
                px="xs"
                py={6}
                className="rounded border border-gray-200 bg-gray-50"
              >
                <IconFile size={14} className="shrink-0 text-gray-500" />
                <Text size="sm" className="flex-1 truncate">
                  {a.title ?? 'Attachment'}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  aria-label={`Remove ${a.title ?? 'attachment'}`}
                  onClick={() => setKeptAttachments((prev) => prev.filter((_, j) => j !== i))}
                >
                  <IconX size={12} />
                </ActionIcon>
              </Group>
            ))}
            {newFiles.map((f, i) => (
              <Group
                key={`new-${i}`}
                gap="xs"
                px="xs"
                py={6}
                className="rounded border border-blue-200 bg-blue-50"
              >
                <IconFile size={14} className="shrink-0 text-blue-500" />
                <Text size="sm" className="flex-1 truncate">
                  {f.name}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => setNewFiles((prev) => prev.filter((_, j) => j !== i))}
                >
                  <IconX size={12} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}

        <Dropzone
          onDrop={handleDrop}
          onReject={() =>
            showNotification({
              title: 'Invalid file',
              message: 'Only PDF, JPG, and PNG files up to 10 MB are allowed',
              color: 'red',
            })
          }
          accept={{
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
            [MIME_TYPES.pdf]: ['.pdf'],
          }}
          maxSize={MAX_FILE_SIZE}
          disabled={totalFiles >= MAX_ATTACHMENTS}
          className={[
            'rounded-xl border border-dashed p-4',
            totalFiles >= MAX_ATTACHMENTS
              ? 'cursor-not-allowed border-gray-200 opacity-50'
              : 'border-slate-300',
          ].join(' ')}
        >
          <Group justify="center" gap="xs" className="text-slate-500">
            <IconUpload size={16} />
            <Text size="sm">
              {totalFiles >= MAX_ATTACHMENTS
                ? 'Maximum attachments reached'
                : remainingSlots < MAX_ATTACHMENTS
                  ? `Drop PDF, JPG, or PNG here (${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} remaining)`
                  : 'Drop PDF, JPG, or PNG here, or click to select'}
            </Text>
          </Group>
        </Dropzone>
      </div>

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={loading} disabled={!note.trim()}>
          Save
        </Button>
      </Group>
    </Stack>
  )
}
